// The landing page for every emailed password link (forgot-password recovery,
// invited-account activation). Supabase verifies the token server-side and
// redirects here with a recovery session; the user is authenticated but must
// still choose a password via supabase.auth.updateUser. Expired/used links
// arrive with error params in the URL fragment instead of a session.
import { useState } from "react";
import { useAuth } from "@/shared/contexts/AuthContext";
import { useLanguage } from "@/shared/i18n/LanguageContext";
import { supabase } from "@/shared/lib/supabase";
import { ArrowLeft, CheckCircle2, KeyRound } from "lucide-react";
import { AuthShell } from "@/shared/components/auth/AuthShell";
import { ac } from "@/shared/components/auth/authCopy";

const NAVY = "#0c1a33";

// Supabase appends error params to the fragment when a link is expired or
// already used (e.g. #error=access_denied&error_code=otp_expired). Read them
// once at first render, before supabase-js strips the fragment.
function readLinkError(): string | null {
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const query = new URLSearchParams(window.location.search);
  return (
    hash.get("error_code") ??
    query.get("error_code") ??
    hash.get("error") ??
    query.get("error")
  );
}

export function ResetPasswordPage() {
  const { user, loading: authLoading } = useAuth();
  const { locale } = useLanguage();
  const [linkError] = useState<string | null>(readLinkError);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const t = (map: Record<string, string>) => map[locale] ?? map.en;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError(
        t({
          en: "Passwords do not match.",
          uk: "Паролі не збігаються.",
          pl: "Hasła nie są zgodne.",
          ro: "Parolele nu se potrivesc.",
        }),
      );
      return;
    }
    if (password.length < 6) {
      setError(
        t({
          en: "Password must be at least 6 characters.",
          uk: "Пароль має бути щонайменше 6 символів.",
          pl: "Hasło musi mieć co najmniej 6 znaków.",
          ro: "Parola trebuie să aibă cel puțin 6 caractere.",
        }),
      );
      return;
    }
    setSaving(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    if (updateError) {
      setError(updateError.message);
      setSaving(false);
      return;
    }
    setDone(true);
    setSaving(false);
  };

  const labelCls = "au-mono block text-[0.625rem] mb-1.5";
  const labelStyle = { color: "rgba(12,26,51,0.6)" };
  const inputCls =
    "w-full rounded-[3px] border border-[rgba(12,26,51,0.18)] bg-white px-3.5 py-2.5 text-[0.9rem] text-[#0c1a33] placeholder-[rgba(12,26,51,0.35)] outline-none transition-all focus:border-[var(--auth-accent)] focus:ring-[3px] focus:ring-[var(--auth-accent-soft)]";
  const accentBtn =
    "w-full py-2.5 rounded-[3px] bg-[var(--auth-accent)] text-white text-[0.8rem] font-semibold tracking-wide hover:brightness-110 transition-all cursor-pointer active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed";

  let body: React.ReactNode;

  if (done) {
    body = (
      <div className="text-center">
        <div
          className="w-12 h-12 mx-auto mb-5 rounded-full flex items-center justify-center"
          style={{
            background: "#fff",
            boxShadow: "inset 0 0 0 1px rgba(12,26,51,0.14)",
          }}
        >
          <CheckCircle2 size={22} style={{ color: NAVY }} />
        </div>
        <h1
          className="au-display text-2xl font-semibold mb-2"
          style={{ color: NAVY }}
        >
          {t({
            en: "Password updated",
            uk: "Пароль оновлено",
            pl: "Hasło zaktualizowane",
            ro: "Parola a fost actualizată",
          })}
        </h1>
        <p
          className="text-sm mb-8 leading-relaxed"
          style={{ color: "rgba(12,26,51,0.55)" }}
        >
          {t({
            en: "You're signed in with your new password.",
            uk: "Ви увійшли з новим паролем.",
            pl: "Jesteś zalogowany(-a) z nowym hasłem.",
            ro: "Ești autentificat(ă) cu noua parolă.",
          })}
        </p>
        <button
          onClick={() => (window as any).navigate("/dashboard")}
          className={accentBtn}
        >
          {t({
            en: "Continue",
            uk: "Продовжити",
            pl: "Kontynuuj",
            ro: "Continuă",
          })}
        </button>
      </div>
    );
  } else if (authLoading && !linkError) {
    body = (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-[var(--auth-accent)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  } else if (linkError || !user) {
    // Expired/used link, or a direct visit with no recovery session.
    body = (
      <div className="text-center">
        <div
          className="w-12 h-12 mx-auto mb-5 rounded-full flex items-center justify-center"
          style={{
            background: "#fff",
            boxShadow: "inset 0 0 0 1px rgba(12,26,51,0.14)",
          }}
        >
          <KeyRound size={22} style={{ color: NAVY }} />
        </div>
        <h1
          className="au-display text-2xl font-semibold mb-2"
          style={{ color: NAVY }}
        >
          {t({
            en: "This link has expired",
            uk: "Це посилання застаріло",
            pl: "Ten link wygasł",
            ro: "Acest link a expirat",
          })}
        </h1>
        <p
          className="text-sm mb-8 leading-relaxed"
          style={{ color: "rgba(12,26,51,0.55)" }}
        >
          {t({
            en: "Password links are single-use and expire quickly. Request a fresh one and try again.",
            uk: "Посилання для пароля одноразові та швидко застарівають. Запросіть нове та спробуйте ще раз.",
            pl: "Linki do hasła są jednorazowe i szybko wygasają. Poproś o nowy i spróbuj ponownie.",
            ro: "Linkurile pentru parolă sunt de unică folosință și expiră repede. Solicită unul nou și încearcă din nou.",
          })}
        </p>
        <button
          onClick={() => (window as any).navigate("/forgot-password")}
          className={accentBtn}
        >
          {t({
            en: "Request a new link",
            uk: "Запросити нове посилання",
            pl: "Poproś o nowy link",
            ro: "Solicită un link nou",
          })}
        </button>
        <p className="text-center text-sm mt-6">
          <button
            onClick={() => (window as any).navigate("/login")}
            className="inline-flex items-center gap-1.5 font-medium cursor-pointer"
            style={{ color: "var(--auth-accent)" }}
          >
            <ArrowLeft size={14} />
            {t({
              en: "Back to login",
              uk: "Назад до входу",
              pl: "Powrót do logowania",
              ro: "Înapoi la autentificare",
            })}
          </button>
        </p>
      </div>
    );
  } else {
    body = (
      <>
        <h1
          className="au-display text-2xl font-semibold mb-1"
          style={{ color: NAVY }}
        >
          {t({
            en: "Choose a new password",
            uk: "Оберіть новий пароль",
            pl: "Wybierz nowe hasło",
            ro: "Alege o parolă nouă",
          })}
        </h1>
        <p className="text-sm mb-8" style={{ color: "rgba(12,26,51,0.55)" }}>
          {t({
            en: `Setting the password for ${user.email}.`,
            uk: `Встановлюємо пароль для ${user.email}.`,
            pl: `Ustawianie hasła dla ${user.email}.`,
            ro: `Setăm parola pentru ${user.email}.`,
          })}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="password" className={labelCls} style={labelStyle}>
              {t({
                en: "New password",
                uk: "Новий пароль",
                pl: "Nowe hasło",
                ro: "Parolă nouă",
              })}
            </label>
            <input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
              autoComplete="new-password"
              className={inputCls}
            />
          </div>
          <div>
            <label
              htmlFor="confirmPassword"
              className={labelCls}
              style={labelStyle}
            >
              {t({
                en: "Confirm password",
                uk: "Підтвердіть пароль",
                pl: "Potwierdź hasło",
                ro: "Confirmă parola",
              })}
            </label>
            <input
              id="confirmPassword"
              type="password"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              className={inputCls}
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-[3px] p-3">
              {error}
            </div>
          )}

          <button type="submit" disabled={saving} className={accentBtn}>
            {saving
              ? t({
                  en: "Saving...",
                  uk: "Зберігаємо...",
                  pl: "Zapisywanie...",
                  ro: "Se salvează...",
                })
              : t({
                  en: "Set password",
                  uk: "Встановити пароль",
                  pl: "Ustaw hasło",
                  ro: "Setează parola",
                })}
          </button>
        </form>
      </>
    );
  }

  return (
    <AuthShell
      locale={locale}
      railTitle={ac("loginTitle", locale)}
      railFooter={ac("trust", locale)}
    >
      {body}
    </AuthShell>
  );
}
